import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import { useStore } from "@/store/StoreContext";
import { formatIDR } from "@/lib/format";
import { waLink, waListMessage } from "@/lib/whatsapp";

export default function CartPage() {
  const { cart, updateQty, removeFromCart, subtotal, clearCart } = useStore();

  return (
    <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 lg:px-16 lg:py-24" data-testid="cart-page">
      <p className="label-eyebrow text-sand">Daftar Pilihan</p>
      <h1 className="mt-5 font-display text-4xl leading-tight text-ink lg:text-5xl">Pilihan Kamu</h1>
      <p className="mt-5 max-w-xl text-sm leading-relaxed text-stone">
        Kumpulkan gelang yang kamu suka di sini, lalu kirim daftarnya lewat WhatsApp. Tidak ada
        pembayaran di website — semua diproses lewat chat.
      </p>

      {cart.length === 0 ? (
        <div className="mt-12 border-t border-line pt-12" data-testid="cart-empty">
          <p className="text-base text-stone">Belum ada gelang yang kamu pilih.</p>
          <Link
            to="/koleksi"
            className="mt-8 inline-flex items-center gap-3 bg-ink px-9 py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
            data-testid="cart-empty-shop-cta"
          >
            Lihat Koleksi <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-7" data-testid="cart-items">
            <div className="border-t border-line">
              {cart.map((item) => (
                <div
                  key={item.slug}
                  className="flex gap-5 border-b border-line py-7"
                  data-testid={`cart-item-${item.slug}`}
                >
                  <Link to={`/produk/${item.slug}`} className="shrink-0 overflow-hidden bg-linen">
                    <img src={item.image} alt={item.name} className="h-28 w-28 object-cover" />
                  </Link>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <Link to={`/produk/${item.slug}`}>
                        <h3 className="font-display text-xl leading-snug text-ink">{item.name}</h3>
                      </Link>
                      <button
                        onClick={() => removeFromCart(item.slug)}
                        className="p-1 text-stone transition-colors duration-300 hover:text-ink"
                        aria-label="Hapus"
                        data-testid={`cart-remove-${item.slug}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-stone">{formatIDR(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between pt-5">
                      <div className="flex items-center border border-line">
                        <button
                          onClick={() => updateQty(item.slug, item.qty - 1)}
                          className="px-3 py-2 text-ink transition-colors duration-300 hover:bg-linen"
                          aria-label="Kurangi"
                          data-testid={`cart-qty-minus-${item.slug}`}
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-10 text-center text-sm" data-testid={`cart-qty-${item.slug}`}>
                          {item.qty}
                        </span>
                        <button
                          onClick={() => updateQty(item.slug, item.qty + 1)}
                          className="px-3 py-2 text-ink transition-colors duration-300 hover:bg-linen"
                          aria-label="Tambah"
                          data-testid={`cart-qty-plus-${item.slug}`}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <p className="text-sm font-medium text-ink" data-testid={`cart-line-total-${item.slug}`}>
                        {formatIDR(item.price * item.qty)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-8">
              <Link
                to="/koleksi"
                className="link-underline text-[12px] uppercase tracking-[0.18em] text-ink"
                data-testid="continue-shopping"
              >
                Lanjut Lihat Koleksi
              </Link>
              <button
                onClick={clearCart}
                className="link-underline text-[12px] uppercase tracking-[0.18em] text-stone"
                data-testid="clear-cart"
              >
                Kosongkan Daftar
              </button>
            </div>
          </div>

          <aside className="lg:col-span-5 lg:col-start-8">
            <div className="bg-linen p-8 lg:sticky lg:top-28" data-testid="cart-summary">
              <p className="label-eyebrow text-sand">Ringkasan</p>
              <dl className="mt-7 space-y-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-stone">Jumlah barang</dt>
                  <dd className="text-ink">{cart.reduce((s, i) => s + i.qty, 0)} pcs</dd>
                </div>
                <div className="flex justify-between border-t border-line pt-5">
                  <dt className="font-display text-xl text-ink">Total</dt>
                  <dd className="font-display text-xl text-ink" data-testid="cart-total">
                    {formatIDR(subtotal)}
                  </dd>
                </div>
              </dl>
              <a
                href={waLink(waListMessage(cart))}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex w-full items-center justify-center gap-3 bg-ink py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
                data-testid="send-whatsapp-button"
              >
                Kirim Daftar via WhatsApp <ArrowRight size={15} />
              </a>
              <p className="mt-4 text-[11px] leading-relaxed text-stone">
                Ongkos kirim dan ketersediaan dikonfirmasi lewat chat.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
